import React, { useEffect, useState } from 'react';
import { RiskMitigationBox } from '../components/RiskMitigationBox';
import { QuarterlyMonthlyToggle } from '../components/QuarterlyMonthlyToggle';
import { MonthlyBarChart } from '../components/MonthlyBarChart';
import { getCurrentQuarterIndex, getQuarterStatus, getMonthStatus, FISCAL_MONTH_ORDER, getMonthLabel } from '../utils/fiscalYear';
import api from '../services/api';

// Helper function to format currency
const formatCurrency = (value: number): string => {
  if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `£${Math.round(value / 1000)}K`;
  return `£${value}`;
};

// Vertical Quarterly Bar Chart Component with dynamic bars based on quarter status
const VerticalQuarterlyChart: React.FC<{
  quarter: string;
  budget: number;
  actual: number;
  forecast: number;
  quarterStatus: 'past' | 'current' | 'future';
}> = ({ quarter, budget, actual, forecast, quarterStatus }) => {
  // Calculate values based on quarter status
  // Past: show actual only, Current: show actual + forecast stacked, Future: show forecast only
  const secondBarValue = quarterStatus === 'past' ? actual :
                         quarterStatus === 'future' ? forecast :
                         actual + forecast;

  const maxValue = Math.max(budget, secondBarValue, 1);
  const budgetHeight = (budget / maxValue) * 100;
  const actualHeight = (actual / maxValue) * 100;
  const forecastHeight = (forecast / maxValue) * 100;

  const isOnTrack = secondBarValue >= budget * 0.95;

  // Determine label for second bar
  const getSecondBarLabel = () => {
    if (quarterStatus === 'past') return { label: 'Actual', color: 'text-green-400' };
    if (quarterStatus === 'future') return { label: 'Forecast', color: 'text-orange-400' };
    return { label: 'Actual/Forecast', color: 'text-white' };
  };

  const secondBarInfo = getSecondBarLabel();

  return (
    <div className="bg-black border border-white rounded-lg p-4">
      <h3 className="text-white font-bold text-lg mb-4 text-center">{quarter}</h3>

      {/* Vertical Bar Chart */}
      <div className="flex justify-center items-end gap-4 h-40 mb-4">
        {/* Budget Bar */}
        <div className="flex flex-col items-center">
          <span className="text-white text-xs font-bold mb-1">{formatCurrency(budget)}</span>
          <div className="w-12 h-32 bg-gray-800 rounded relative flex items-end">
            <div
              className="w-full bg-blue-500 rounded transition-all duration-500"
              style={{ height: `${budgetHeight}%` }}
            />
          </div>
          <span className="text-blue-400 text-xs font-semibold mt-2">Budget</span>
        </div>

        {/* Second Bar - varies based on quarter status */}
        <div className="flex flex-col items-center">
          <span className="text-white text-xs font-bold mb-1">{formatCurrency(secondBarValue)}</span>
          <div className="w-12 h-32 bg-gray-800 rounded relative flex flex-col justify-end">
            {quarterStatus === 'past' && (
              <div
                className="w-full bg-green-500 rounded transition-all duration-500"
                style={{ height: `${actualHeight}%` }}
              />
            )}
            {quarterStatus === 'current' && (
              <>
                <div
                  className="w-full bg-orange-500 rounded-t transition-all duration-500"
                  style={{ height: `${forecastHeight}%` }}
                />
                <div
                  className="w-full bg-green-500 rounded-b transition-all duration-500"
                  style={{ height: `${actualHeight}%` }}
                />
              </>
            )}
            {quarterStatus === 'future' && (
              <div
                className="w-full bg-orange-500 rounded transition-all duration-500"
                style={{ height: `${forecastHeight}%` }}
              />
            )}
          </div>
          <span className={`${secondBarInfo.color} text-xs font-semibold mt-2 text-center`}>{secondBarInfo.label}</span>
        </div>
      </div>

      {/* Status Badge */}
      <div className="text-center">
        <span className={`inline-block px-3 py-1 rounded text-white text-xs font-bold ${
          isOnTrack ? 'bg-green-500' : 'bg-orange-500'
        }`}>
          {isOnTrack ? 'On Track' : 'Below Target'}
        </span>
      </div>
    </div>
  );
};

interface ContentServicesDashboardProps {
  data: any;
}

export const ContentServicesDashboard: React.FC<ContentServicesDashboardProps> = ({ data }) => {
  // Get current quarter for determining past/current/future status
  const currentQuarter = getCurrentQuarterIndex();
  const [granularity, setGranularity] = useState<'quarterly' | 'monthly'>('quarterly');
  // Monthly data + history are fetched on demand from the department endpoint
  // (filtered client-side to this rep's territories) so we don't have to
  // extend the legacy /sales-rep-dashboard endpoint.
  const [monthlyData, setMonthlyData] = useState<any | null>(null);
  const [history, setHistory] = useState<any | null>(null);

  useEffect(() => {
    if (granularity !== 'monthly') return;
    if (monthlyData) return; // cached
    Promise.all([
      api.get('/dashboard/departments-detailed', { params: { granularity: 'monthly' } }),
      api.get('/dashboard/history/summary').catch(() => ({ data: null })),
    ])
      .then(([dr, hr]) => {
        if (dr?.data?.success) setMonthlyData(dr.data.data.contentServices);
        if (hr?.data?.success) setHistory(hr.data.data);
      })
      .catch((e) => console.error('Monthly fetch failed', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [granularity]);

  // Filter the all-territories list down to this rep's territories + streams.
  // territoryOverview now contains both territory and stream rows (kind: 'stream'),
  // so we need to look in both monthlyData.allTerritories and monthlyData.revenueStreams.
  const repTerritories = (data.territoryOverview || []).map((t: any) => t.territoryName?.toLowerCase());
  const myMonthlyTerritories = [
    ...(monthlyData?.allTerritories || []),
    ...(monthlyData?.revenueStreams || []),
  ].filter((t: any) => repTerritories.includes(t.name?.toLowerCase()));
  const myHistory = (history?.byTerritory || []).filter(
    (h: any) => repTerritories.includes(h.territoryName?.toLowerCase())
  );

  // Aggregate monthlyPerformance for this rep's territories only
  const myMonthlyAgg = (() => {
    if (!monthlyData) return null;
    const months = FISCAL_MONTH_ORDER.map((m) => ({ month: m, label: getMonthLabel(m), budget: 0, forecast: 0, actual: 0 }));
    for (const t of myMonthlyTerritories) {
      for (const qKey of ['q1Months', 'q2Months', 'q3Months', 'q4Months'] as const) {
        for (const md of (t[qKey] || []) as Array<{ month: number; budget: number; forecast: number; actual: number }>) {
          const target = months.find((x) => x.month === md.month);
          if (target) {
            target.budget += md.budget;
            target.forecast += md.forecast;
            target.actual += md.actual;
          }
        }
      }
    }
    return months;
  })();

  // Build previous-actual lookup per month (sum across this rep's territories)
  const myPrevByMonth: Record<number, number | null> = {};
  if (history) {
    for (const m of FISCAL_MONTH_ORDER) {
      const baselines = myHistory
        .map((h: any) => h.months?.find((x: any) => x.periodNumber === m)?.previous?.actual)
        .filter((v: any): v is number => typeof v === 'number');
      myPrevByMonth[m] = baselines.length > 0 ? baselines.reduce((s: number, v: number) => s + v, 0) : null;
    }
  }

  // Calculate totals from quarterly data with quarter-status awareness
  // Past quarters: count actual only (forecast is stale)
  // Current quarter: count actual + forecast
  // Future quarters: count forecast only
  const totalBudget = data.quarterlyChartData?.reduce((sum: number, q: any) => sum + q.budget, 0) || 0;

  let totalActual = 0;
  let totalForecast = 0;

  data.quarterlyChartData?.forEach((q: any, index: number) => {
    const status = getQuarterStatus(index, currentQuarter);

    if (status === 'past') {
      // Past: only count actual (forecast is no longer relevant)
      totalActual += q.actual || 0;
    } else if (status === 'current') {
      // Current: count both actual and remaining forecast
      totalActual += q.actual || 0;
      totalForecast += q.forecast || 0;
    } else {
      // Future: only count forecast (no actual yet)
      totalForecast += q.forecast || 0;
    }
  });

  return (
    <div className="bg-background">
      <main className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">{data.rep.fullName}</h1>
            <p className="text-gray-400">{data.rep.department} - Territory Dashboard</p>
          </div>
          <QuarterlyMonthlyToggle value={granularity} onChange={setGranularity} />
        </div>

        {/* Full Year Summary - Horizontal Bar Chart */}
        <section className="mb-12">
          <div className="bg-black border-2 border-green-500 rounded-lg p-6">
            <h2 className="text-xl font-bold text-green-500 mb-6 text-center">FY26 Full Year Summary</h2>
            {(() => {
              const totalProgress = totalActual + totalForecast;
              const maxValue = Math.max(totalBudget, totalProgress, 1);
              const budgetWidth = (totalBudget / maxValue) * 100;
              const actualWidth = (totalActual / maxValue) * 100;
              const forecastWidth = (totalForecast / maxValue) * 100;
              const isOnTrack = totalProgress >= totalBudget * 0.95;

              return (
                <div className="space-y-6">
                  {/* Budget Bar */}
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-blue-400 text-sm font-semibold">Budget</span>
                      <span className="text-white text-sm font-bold">{formatCurrency(totalBudget)}</span>
                    </div>
                    <div className="h-10 bg-gray-800 rounded overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded transition-all duration-500"
                        style={{ width: `${budgetWidth}%` }}
                      />
                    </div>
                  </div>

                  {/* Actual + Forecast Stacked Bar */}
                  <div>
                    <div className="flex justify-between mb-2">
                      <div className="flex items-center gap-4">
                        <span className="text-green-400 text-sm font-semibold">Actual</span>
                        <span className="text-white text-sm">{formatCurrency(totalActual)}</span>
                        <span className="text-gray-500">+</span>
                        <span className="text-orange-400 text-sm font-semibold">Forecast</span>
                        <span className="text-white text-sm">{formatCurrency(totalForecast)}</span>
                      </div>
                      <span className="text-white text-sm font-bold">= {formatCurrency(totalProgress)}</span>
                    </div>
                    <div className="h-10 bg-gray-800 rounded overflow-hidden flex">
                      <div
                        className="h-full bg-green-500 transition-all duration-500"
                        style={{ width: `${actualWidth}%` }}
                      />
                      <div
                        className="h-full bg-orange-500 transition-all duration-500"
                        style={{ width: `${forecastWidth}%` }}
                      />
                    </div>
                  </div>

                  {/* Status indicator */}
                  <div className="flex justify-center">
                    <span className={`inline-block px-4 py-2 rounded text-white text-sm font-bold ${
                      isOnTrack ? 'bg-green-500' : 'bg-orange-500'
                    }`}>
                      {isOnTrack ? 'On Track' : 'Below Target'} ({Math.round((totalProgress / totalBudget) * 100)}% of Budget)
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
        </section>

        {/* Quarterly / Monthly Bar Charts */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">
            FY26 {granularity === 'monthly' ? 'Monthly' : 'Quarterly'} Performance
          </h2>
          {granularity === 'monthly' ? (
            !monthlyData ? (
              <div className="bg-black border border-white rounded-lg p-6 text-center">
                <p className="text-sm text-gray-400">Loading monthly data…</p>
              </div>
            ) : !myMonthlyAgg || myMonthlyAgg.every((m) => !m.budget && !m.actual && !m.forecast) ? (
              <div className="bg-black border border-white rounded-lg p-6 text-center">
                <p className="text-sm text-gray-400">No monthly data available yet for this rep's territories.</p>
              </div>
            ) : (
              <>
                {history?.hasBaseline && (
                  <p className="text-xs text-gray-400 mb-3">
                    Δ shown vs last meeting baseline:{' '}
                    <span className="text-white font-medium">
                      {new Date(history.baselineDate).toLocaleDateString()}
                    </span>{' '}
                    ({history.daysSinceBaseline} days ago)
                  </p>
                )}
                <MonthlyBarChart data={myMonthlyAgg} previousActualByMonth={myPrevByMonth} />
              </>
            )
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {data.quarterlyChartData?.map((quarter: any, index: number) => (
                <VerticalQuarterlyChart
                  key={quarter.quarter}
                  quarter={quarter.quarter}
                  budget={quarter.budget}
                  actual={quarter.actual}
                  forecast={quarter.forecast}
                  quarterStatus={getQuarterStatus(index, currentQuarter)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Risk and Mitigation Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">Risks & Mitigations</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RiskMitigationBox
              title="Risks"
              items={data.riskItems || []}
              type="risk"
            />
            <RiskMitigationBox
              title="Mitigations"
              items={data.mitigationItems || []}
              type="mitigation"
            />
          </div>
        </section>

        {/* Territory Overview Table — switches columns based on toggle */}
        {data.territoryOverview && data.territoryOverview.length > 0 && (
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6">
              {granularity === 'monthly' ? 'Monthly Breakdown' : 'Territory Overview'}
            </h2>
            <div className="bg-black border border-white rounded-lg overflow-x-auto">
              {granularity === 'monthly' ? (() => {
                // Use monthly data filtered to this rep (territories + streams)
                const rows = myMonthlyTerritories;
                const monthLabels: Record<number, string> = {
                  1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun',
                  7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec',
                };
                const getMonthData = (t: any, m: number) => {
                  const allMonths = [...(t.q1Months || []), ...(t.q2Months || []), ...(t.q3Months || []), ...(t.q4Months || [])];
                  return allMonths.find((x: any) => x.month === m);
                };
                const getMonthValue = (t: any, m: number): number | undefined => {
                  const md = getMonthData(t, m);
                  if (!md) return undefined;
                  const status = getMonthStatus(m);
                  return status === 'past' ? md.actual : status === 'future' ? md.forecast : md.actual + md.forecast;
                };
                if (!monthlyData) {
                  return <p className="text-sm text-gray-400 text-center py-6">Loading monthly data…</p>;
                }
                if (rows.length === 0) {
                  return <p className="text-sm text-gray-400 text-center py-6">No monthly data available for this rep's territories yet.</p>;
                }
                return (
                  <table className="w-full">
                    <thead className="bg-gray-900">
                      <tr>
                        <th className="px-4 py-3 text-left text-white font-semibold sticky left-0 bg-gray-900">Territory</th>
                        {FISCAL_MONTH_ORDER.map((m) => (
                          <th key={m} className="px-2 py-3 text-right text-white text-xs font-semibold">{monthLabels[m]}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((t: any) => (
                        <tr key={t.id} className="border-t border-gray-800">
                          <td className="px-4 py-3 text-white font-medium sticky left-0 bg-black">
                            {t.name}
                            {t.kind === 'stream' && (
                              <span className="ml-2 px-1.5 py-0.5 bg-blue-900/40 text-blue-300 text-[10px] rounded">Stream</span>
                            )}
                          </td>
                          {FISCAL_MONTH_ORDER.map((m) => {
                            const md = getMonthData(t, m);
                            const val = getMonthValue(t, m);
                            const status = getMonthStatus(m);
                            const budget = md?.budget || 0;
                            const onTrack = (val ?? 0) >= budget * 0.95;
                            return (
                              <td key={m} className={`px-2 py-3 text-right text-xs ${
                                val === undefined || val === 0 ? 'text-gray-500' :
                                status === 'past' ? (onTrack ? 'text-green-400' : 'text-red-400') :
                                status === 'current' ? 'text-white' :
                                'text-orange-400'
                              }`}>
                                {val !== undefined && val > 0 ? formatCurrency(val) : '-'}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })() : (
                <table className="w-full">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-6 py-4 text-left text-white font-semibold">Territory</th>
                      <th colSpan={3} className="px-6 py-4 text-center text-white font-semibold border-l border-gray-700">Q1</th>
                      <th colSpan={3} className="px-6 py-4 text-center text-white font-semibold border-l border-gray-700">Q2</th>
                      <th colSpan={3} className="px-6 py-4 text-center text-white font-semibold border-l border-gray-700">Q3</th>
                      <th colSpan={3} className="px-6 py-4 text-center text-white font-semibold border-l border-gray-700">Q4</th>
                    </tr>
                    <tr>
                      <th className="px-6 py-3 text-left text-gray-400 font-medium text-sm"></th>
                      <th className="px-3 py-3 text-center text-gray-400 font-medium text-sm border-l border-gray-700">Budget</th>
                      <th className="px-3 py-3 text-center text-gray-400 font-medium text-sm">Forecast</th>
                      <th className="px-3 py-3 text-center text-gray-400 font-medium text-sm">Actual</th>
                      <th className="px-3 py-3 text-center text-gray-400 font-medium text-sm border-l border-gray-700">Budget</th>
                      <th className="px-3 py-3 text-center text-gray-400 font-medium text-sm">Forecast</th>
                      <th className="px-3 py-3 text-center text-gray-400 font-medium text-sm">Actual</th>
                      <th className="px-3 py-3 text-center text-gray-400 font-medium text-sm border-l border-gray-700">Budget</th>
                      <th className="px-3 py-3 text-center text-gray-400 font-medium text-sm">Forecast</th>
                      <th className="px-3 py-3 text-center text-gray-400 font-medium text-sm">Actual</th>
                      <th className="px-3 py-3 text-center text-gray-400 font-medium text-sm border-l border-gray-700">Budget</th>
                      <th className="px-3 py-3 text-center text-gray-400 font-medium text-sm">Forecast</th>
                      <th className="px-3 py-3 text-center text-gray-400 font-medium text-sm">Actual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.territoryOverview.map((territory: any) => (
                      <tr key={territory.territoryId} className="border-t border-gray-800">
                        <td className="px-6 py-4 text-white font-semibold">
                          {territory.territoryName}
                          {territory.kind === 'stream' && (
                            <span className="ml-2 px-1.5 py-0.5 bg-blue-900/40 text-blue-300 text-[10px] rounded">Stream</span>
                          )}
                        </td>
                        <td className="px-3 py-4 text-center text-white border-l border-gray-800">{formatCurrency(territory.q1.budget)}</td>
                        <td className="px-3 py-4 text-center text-white">{formatCurrency(territory.q1.forecast)}</td>
                        <td className="px-3 py-4 text-center text-white">{territory.q1.actual > 0 ? formatCurrency(territory.q1.actual) : '-'}</td>
                        <td className="px-3 py-4 text-center text-white border-l border-gray-800">{formatCurrency(territory.q2.budget)}</td>
                        <td className="px-3 py-4 text-center text-white">{formatCurrency(territory.q2.forecast)}</td>
                        <td className="px-3 py-4 text-center text-white">{territory.q2.actual > 0 ? formatCurrency(territory.q2.actual) : '-'}</td>
                        <td className="px-3 py-4 text-center text-white border-l border-gray-800">{formatCurrency(territory.q3.budget)}</td>
                        <td className="px-3 py-4 text-center text-white">{formatCurrency(territory.q3.forecast)}</td>
                        <td className="px-3 py-4 text-center text-white">{territory.q3.actual > 0 ? formatCurrency(territory.q3.actual) : '-'}</td>
                        <td className="px-3 py-4 text-center text-white border-l border-gray-800">{formatCurrency(territory.q4.budget)}</td>
                        <td className="px-3 py-4 text-center text-white">{formatCurrency(territory.q4.forecast)}</td>
                        <td className="px-3 py-4 text-center text-white">{territory.q4.actual > 0 ? formatCurrency(territory.q4.actual) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};
